import React from "react";
import { cn } from "@/lib/util";

export interface LogoProps {
    color?: string;
    className?: string;
    size?: string | number;
}

type LogoSvgProps = React.ComponentPropsWithoutRef<"svg">;

export const Logo = React.forwardRef<SVGSVGElement, LogoProps & LogoSvgProps>(
    ({ color = "currentColor", className, size = "1rem", ...rest }, ref) => {
        const sizeValue = typeof size === "number" ? `${size}px` : size;

        // All strokes use the same styling for a clean, editorial look
        const strokeProps = {
            stroke: color ?? "currentColor",
            strokeWidth: 1.5,
            strokeLinecap: "round" as const,
            strokeLinejoin: "round" as const,
            fill: "none" as const,
        };

        return (
            <svg
                ref={ref}
                width={sizeValue}
                height={sizeValue}
                viewBox="0 0 24 24"
                xmlns="http://www.w3.org/2000/svg"
                className={cn(className)}
                focusable="false"
                {...rest}
            >
                {/* Globe outer ring */}
                <circle cx="12" cy="12" r="8.5" {...strokeProps} />

                {/* Meridians and parallels (kept minimal for legibility at small sizes) */}
                <ellipse cx="12" cy="12" rx="5.5" ry="8.5" {...strokeProps} />
                <ellipse cx="12" cy="12" rx="8.5" ry="5.5" {...strokeProps} />

                {/* Shield (protection) - bottom-right overlay */}
                <path
                    d="M16.5 12.5L20.5 14v2.4c0 2.2-1.4 4.2-3.5 4.9-2.1-.7-3.5-2.7-3.5-4.9V14l3-1.5Z"
                    {...strokeProps}
                />

                {/* Heart (what you love) inside the shield */}
                <path
                    d="
                        M18.5 16.1
                        c-0.5-0.9-1.9-0.9-2.4 0
                        c-0.4 0.7-0.2 1.6 0.4 2.1
                        l2.0 1.8
                        l2.0-1.8
                        c0.6-0.5 0.8-1.4 0.4-2.1
                        c-0.5-0.9-1.9-0.9-2.4 0
                        Z
                    "
                    fill={color ?? "currentColor"}
                />
            </svg>
        );
    }
);

Logo.displayName = "Logo";